import { Stack } from 'component-library';
import { Trans, useTranslation } from 'react-i18next';

import { BenchmarkType, PlotReportFactElement } from '@/api/rest/resources/types/fact';
import { NCCardSectionHeader, printLatestDate, printNthYearDate } from '@/components/NCCard/NCCard';
import { getNCChangeIcon, getNCChangeIconVariant, NCFactCapsule } from '@/components/NCFactCapsule/NCFactCapsule';
import { BenchmarkPill } from '@/components/Pill/BenchmarkPill';
import { useBenchmarkForFact } from '@/pages/shared/hooks/useBenchmarkForFact';
import { assertBenchmarkType } from '@/utils/assert';

export const BiodiversityStatsTile = ({
  latestAnalysisFact,
  oneYearUpliftFact,
  benchmarks,
}: {
  latestAnalysisFact: PlotReportFactElement<unknown> | undefined;
  oneYearUpliftFact: PlotReportFactElement<unknown> | undefined;
  benchmarks: number[];
}) => {
  const { t } = useTranslation();

  const latestAnalysisTranslation = t('shared.ncaDetail.details.labels.latestAnalysis');

  const bpBenchmarkType = useBenchmarkForFact(benchmarks, latestAnalysisFact?.value);
  assertBenchmarkType(bpBenchmarkType, 'BiodiversityStatsTile');
  assertBenchmarkType(bpBenchmarkType, 'BiodiversityStatsTile', BenchmarkType.unchanged);
  assertBenchmarkType(bpBenchmarkType, 'BiodiversityStatsTile', BenchmarkType.stable);
  assertBenchmarkType(bpBenchmarkType, 'BiodiversityStatsTile', BenchmarkType.alerted);
  assertBenchmarkType(bpBenchmarkType, 'BiodiversityStatsTile', BenchmarkType.very_high);

  const biodiversityLatestAnalysisLastMeasuredDate = printLatestDate([latestAnalysisFact?.measured_at]);
  const biodiversity1yGrowthLastMeasuredDate = printNthYearDate([oneYearUpliftFact?.measured_at], -1);

  const lastMeasuredDate = printLatestDate([latestAnalysisFact?.measured_at, oneYearUpliftFact?.measured_at]);

  return (
    <Stack className='gap-8 p-6 lg:justify-center lg:gap-4 lg:p-8 lg:pr-2' data-testid='biodiversity-stats-tile'>
      <div className='flex flex-col gap-8 lg:gap-4'>
        <NCCardSectionHeader
          title={t('shared.ncaDetail.details.biodiversity.labels.protectedOnFarmHabitat')}
          supplementaryInfo={`${latestAnalysisTranslation} ${lastMeasuredDate}`}
          popover={{
            title: t('shared.ncaDetail.details.biodiversity.explainers.protectedOnFarmHabitat.title'),
            body: t('shared.ncaDetail.details.biodiversity.explainers.protectedOnFarmHabitat.body'),
          }}
        />
        <Stack direction='row' className='flex-wrap gap-16'>
          <NCFactCapsule
            label={latestAnalysisTranslation}
            capsuleIconProps={{
              icon: getNCChangeIcon(latestAnalysisFact),
              variant: getNCChangeIconVariant(latestAnalysisFact),
            }}
            fact={latestAnalysisFact}
            infoPopoverProps={{
              title: t('shared.ncaDetail.details.explainers.latestAnalysis.title'),
              body: t('shared.ncaDetail.details.explainers.latestAnalysis.body', {
                date: biodiversityLatestAnalysisLastMeasuredDate,
              }),
            }}
          />
          <NCFactCapsule
            label={t('shared.ncaDetail.details.labels.oneYearChange')}
            capsuleIconProps={{
              icon: getNCChangeIcon(oneYearUpliftFact),
              variant: getNCChangeIconVariant(oneYearUpliftFact),
            }}
            fact={oneYearUpliftFact}
            infoPopoverProps={{
              title: t('shared.ncaDetail.details.biodiversity.explainers.oneYearChange.title'),
              body: t('shared.ncaDetail.details.biodiversity.explainers.oneYearChange.body', {
                date: biodiversityLatestAnalysisLastMeasuredDate,
                previousDate: biodiversity1yGrowthLastMeasuredDate,
              }),
            }}
          />
        </Stack>
        {bpBenchmarkType && (
          <Stack className='mt-4 gap-4 rounded-2xl bg-neutral-black-4 px-4 py-6'>
            <BenchmarkPill benchmarkType={bpBenchmarkType} />
            <span className='typography-body1Bold'>
              {t('shared.ncaDetail.details.biodiversity.explainers.bpBenchmark.title')}
            </span>
            <Trans
              i18nKey={`shared.ncaDetail.details.biodiversity.explainers.bpBenchmark.${bpBenchmarkType}`}
              components={{
                p: <p className='typography-body1 text-text-secondary' />,
              }}
            />
          </Stack>
        )}
      </div>
    </Stack>
  );
};
